import { useState, useRef, useCallback } from "react";

export interface ContactFormData {
  subject?: string;
  message?: string;
  htmlMessage?: string;
  name?: string;
  telephone?: string;
  company?: string;
  email?: string;
}

interface UseContactFormReturn {
  submit: (data: ContactFormData) => Promise<boolean>;
  loading: boolean;
  error: string | null;
  success: boolean;
  reset: () => void;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL as string;

export const useContactForm = (): UseContactFormReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
  setError(null);
  setSuccess(false);
}, []);

  const submit = async (data: ContactFormData): Promise<boolean> => {
    if (loading) return false;

    setLoading(true);
    setError(null);
    setSuccess(false);

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(`${API_BASE_URL}/activity/addContact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(data),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to submit form");
      }

      setSuccess(true);
      return true;
    } catch (err: unknown) {
      if ((err as any)?.name === "AbortError") {
        return false;
      }

      setError(
        err instanceof Error
          ? err.message
          : "Unexpected error occurred"
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { submit, loading, error, success, reset };
};